/**
 * @fileoverview Centralized Keyboard Shortcut Management System
 * 
 * This file implements a centralized system for managing keyboard shortcuts
 * across the application. It handles registration, conflict detection, and
 * execution of keyboard shortcuts with priority-based resolution.
 * 
 * Key Features:
 * - Priority-based shortcut resolution (tool shortcuts override global)
 * - Conflict detection with console warnings
 * - Event-driven architecture for loose coupling
 * - Support for modifier keys and key combinations
 * - Tool lifecycle integration
 * 
 * @author Claude Code
 * @version 1.0.0
 */

import { IEventBus } from '../events/IEventBus';
import { IKeyboardShortcut } from '../tools/interfaces/ITool';
import { Logger } from '../../utils/logger';
import { SessionMetrics } from '../../domains/analytics/services/SessionMetrics';

/**
 * Priority levels for keyboard shortcuts.
 * Higher numbers take precedence over lower numbers.
 */
export enum ShortcutPriority {
  GLOBAL = 0,
  TOOL = 10,
  ACTIVE_TOOL = 20
}

/**
 * Represents a registered keyboard shortcut with execution context.
 */
export interface IRegisteredShortcut {
  /** Unique identifier for this shortcut registration */
  id: string;
  
  /** The shortcut definition */
  shortcut: IKeyboardShortcut;
  
  /** Priority level for conflict resolution */
  priority: ShortcutPriority;
  
  /** Function to execute when shortcut is triggered */
  handler: () => void;
  
  /** Source that registered this shortcut (for debugging) */
  source: string;
  
  /** Whether this shortcut is currently active */
  active: boolean;

  /** When false is returned, default browser handling is not suppressed */
  shouldPreventDefault?: (event: KeyboardEvent) => boolean;
}

export type RegisterShortcutOptions = {
  shouldPreventDefault?: (event: KeyboardEvent) => boolean;
};

/**
 * Manages keyboard shortcuts across the application with priority-based resolution.
 * 
 * Provides centralized registration and execution of keyboard shortcuts with
 * conflict detection and resolution. Tool shortcuts automatically override
 * global shortcuts when tools are active.
 * 
 * @example
 * ```typescript
 * const manager = new KeyboardShortcutManager(eventBus);
 * 
 * // Register global shortcut
 * manager.registerShortcut('global-undo', {
 *   key: 'z',
 *   modifiers: ['ctrl'],
 *   description: 'Undo'
 * }, ShortcutPriority.GLOBAL, () => commandManager.undo(), 'global');
 * 
 * // Register tool shortcut (will override global when active)
 * manager.registerShortcut('tool-move', {
 *   key: 'ArrowUp',
 *   modifiers: [],
 *   description: 'Move up'
 * }, ShortcutPriority.TOOL, () => moveTool.moveUp(), 'MovementTool');
 * ```
 */
export class KeyboardShortcutManager {
  /** Map of registered shortcuts by unique ID */
  private shortcuts: Map<string, IRegisteredShortcut> = new Map();
  
  /** Map of key combinations to shortcut IDs for fast lookup */
  private keyMap: Map<string, string[]> = new Map();
  
  /** Event bus for communication */
  private eventBus: IEventBus;
  
  /** Whether the manager is currently listening for keyboard events */
  private isListening = false;
  
  constructor(eventBus: IEventBus) {
    this.eventBus = eventBus;
    this.startListening();
  }
  
  /**
   * Register a keyboard shortcut with the manager.
   * 
   * @param id - Unique identifier for this shortcut
   * @param shortcut - Shortcut definition
   * @param priority - Priority level for conflict resolution
   * @param handler - Function to execute when triggered
   * @param source - Source that registered this shortcut
   * @param active - Whether shortcut is initially active
   * @param options - Optional behavior (e.g. conditional preventDefault)
   */
  registerShortcut(
    id: string,
    shortcut: IKeyboardShortcut,
    priority: ShortcutPriority,
    handler: () => void,
    source: string,
    active = true,
    options?: RegisterShortcutOptions
  ): void {
    const existing = this.shortcuts.get(id);
    if (existing) {
      // Check if it's the exact same registration (idempotent)
      const isSame =
        existing.source === source &&
        existing.priority === priority &&
        this.getKeyCombo(existing.shortcut) === this.getKeyCombo(shortcut);

      if (isSame) {
        // Silently skip duplicate registration
        return;
      }

      // Different shortcut with same ID - unregister old one and register new one
      Logger.warn('KeyboardShortcutManager', `Replacing existing shortcut with id "${id}" (old source: ${existing.source}, new source: ${source})`);
      this.unregisterShortcut(id);
    }

    const keyCombo = this.getKeyCombo(shortcut);
    const registered: IRegisteredShortcut = {
      id,
      shortcut,
      priority,
      handler,
      source,
      active,
      shouldPreventDefault: options?.shouldPreventDefault
    };

    this.shortcuts.set(id, registered);

    // Add to key map for fast lookup
    if (!this.keyMap.has(keyCombo)) {
      this.keyMap.set(keyCombo, []);
    }
    this.keyMap.get(keyCombo)!.push(id);

    // Check for conflicts and warn
    this.checkForConflicts(keyCombo);

    this.eventBus.emit('keyboard:shortcut:registered', { id, shortcut, source });
  }
  
  /**
   * Unregister a keyboard shortcut.
   * 
   * @param id - ID of the shortcut to unregister
   */
  unregisterShortcut(id: string): void {
    const registered = this.shortcuts.get(id);
    if (!registered) {
      return;
    }
    
    const keyCombo = this.getKeyCombo(registered.shortcut);
    
    // Remove from shortcuts map
    this.shortcuts.delete(id);
    
    // Remove from key map
    const keyMapEntry = this.keyMap.get(keyCombo);
    if (keyMapEntry) {
      const index = keyMapEntry.indexOf(id);
      if (index > -1) {
        keyMapEntry.splice(index, 1);
      }
      
      // Clean up empty key map entries
      if (keyMapEntry.length === 0) {
        this.keyMap.delete(keyCombo);
      }
    }
    
    this.eventBus.emit('keyboard:shortcut:unregistered', { id });
  }
  
  /**
   * Activate a shortcut (make it eligible for execution).
   * 
   * @param id - ID of the shortcut to activate
   */
  activateShortcut(id: string): void {
    const shortcut = this.shortcuts.get(id);
    if (shortcut) {
      shortcut.active = true;
      this.eventBus.emit('keyboard:shortcut:activated', { id });
    }
  }
  
  /**
   * Deactivate a shortcut (prevent it from executing).
   * 
   * @param id - ID of the shortcut to deactivate
   */
  deactivateShortcut(id: string): void {
    const shortcut = this.shortcuts.get(id);
    if (shortcut) {
      shortcut.active = false;
      this.eventBus.emit('keyboard:shortcut:deactivated', { id });
    }
  }
  
  /**
   * Get all registered shortcuts for a specific source.
   * 
   * @param source - Source to filter by
   * @returns Array of shortcuts from the specified source
   */
  getShortcutsBySource(source: string): IRegisteredShortcut[] {
    return Array.from(this.shortcuts.values()).filter(s => s.source === source);
  }
  
  /**
   * Get all active shortcuts.
   * 
   * @returns Array of currently active shortcuts
   */
  getActiveShortcuts(): IRegisteredShortcut[] {
    return Array.from(this.shortcuts.values()).filter(s => s.active);
  }
  
  /**
   * Start listening for keyboard events.
   */
  private startListening(): void {
    if (this.isListening) {
      return;
    }
    
    window.addEventListener('keydown', this.handleKeyDown);
    this.isListening = true;
  }
  
  /**
   * Stop listening for keyboard events.
   */
  stopListening(): void {
    if (!this.isListening) {
      return;
    }
    
    window.removeEventListener('keydown', this.handleKeyDown);
    this.isListening = false;
  }
  
  /**
   * Handle keyboard events and execute matching shortcuts.
   */
  private handleKeyDown = (event: KeyboardEvent): void => {
    // Avoid auto-repeat for modifier combos (e.g., Cmd+V pasting multiple times)
    if (event.repeat && (event.ctrlKey || event.metaKey)) {
      return;
    }
    // Skip if typing in input fields
    if (this.isTypingInInput(event.target)) {
      return;
    }
    
    const keyCombo = this.getKeyComboFromEvent(event);
    const shortcutIds = this.keyMap.get(keyCombo);
    
    if (!shortcutIds || shortcutIds.length === 0) {
      return;
    }
    
    // Find the highest priority active shortcut
    let bestShortcut: IRegisteredShortcut | null = null;
    let highestPriority = -1;
    
    for (const id of shortcutIds) {
      const shortcut = this.shortcuts.get(id);
      if (shortcut && shortcut.active && shortcut.priority > highestPriority) {
        bestShortcut = shortcut;
        highestPriority = shortcut.priority;
      }
    }
    
    if (bestShortcut) {
      const prevent =
        bestShortcut.shouldPreventDefault?.(event) ?? true;
      if (prevent) {
        event.preventDefault();
        event.stopPropagation();
      }

      try {
        bestShortcut.handler();
        this.eventBus.emit('keyboard:shortcut:executed', {
          id: bestShortcut.id,
          shortcut: bestShortcut.shortcut,
          source: bestShortcut.source
        });
        // Track keyboard shortcut usage (sampled + first-time)
        SessionMetrics.sampled('keyboard_shortcut', { shortcut: bestShortcut.id });
        SessionMetrics.firstTime('keyboard_shortcut');
      } catch (error) {
        console.error(`[KeyboardShortcutManager] Error executing shortcut "${bestShortcut.id}":`, error);
        this.eventBus.emit('keyboard:shortcut:error', {
          id: bestShortcut.id,
          error
        });
      }
    }
  };
  
  /**
   * Check if the user is currently typing in an input field.
   */
  private isTypingInInput(target: EventTarget | null): boolean {
    if (!target || !(target instanceof Element)) return false;

    const tagName = target.tagName.toLowerCase();
    const isInput = tagName === 'input' || tagName === 'textarea';
    const isContentEditable = target.getAttribute('contenteditable') === 'true';
    
    return isInput || isContentEditable;
  }
  
  /**
   * Generate a key combination string from a shortcut definition.
   */
  private getKeyCombo(shortcut: IKeyboardShortcut): string {
    const modifiers = (shortcut.modifiers || [])
      .map(mod => mod.toLowerCase())
      .slice()
      .sort();
    const key = (shortcut.key || '').toLowerCase();
    return [...modifiers, key].join('+');
  }
  
  /**
   * Generate a key combination string from a keyboard event.
   */
  private getKeyComboFromEvent(event: KeyboardEvent): string {
    const modifiers: string[] = [];
    
    if (event.ctrlKey) modifiers.push('ctrl');
    if (event.metaKey) modifiers.push('cmd');
    if (event.shiftKey) modifiers.push('shift');
    if (event.altKey) modifiers.push('alt');
    
    modifiers.sort();
    
    let key = event.key || '';
    if (key.length === 1) {
      key = key.toLowerCase();
    } else {
      key = key.toLowerCase();
    }
    
    return [...modifiers, key].join('+');
  }
  
  /**
   * Check for conflicts between shortcuts and warn in console.
   */
  private checkForConflicts(keyCombo: string): void {
    const shortcutIds = this.keyMap.get(keyCombo);
    if (!shortcutIds || shortcutIds.length <= 1) {
      return;
    }

    // A key combo is only genuinely ambiguous when more than one *active* shortcut
    // shares it *at the same priority* — that's the case with no discriminator to
    // decide which handler fires. Inactive registrations (tool shortcuts register
    // inactive and are context-gated by canExecute) and different-priority bindings
    // (higher priority wins deterministically) are not conflicts, so warning on them
    // produced false positives across context-scoped tools.
    const active = shortcutIds
      .map(id => this.shortcuts.get(id))
      .filter((s): s is IRegisteredShortcut => Boolean(s) && s!.active);

    if (active.length <= 1) {
      return;
    }

    const topPriority = Math.max(...active.map(s => s.priority));
    const contenders = active.filter(s => s.priority === topPriority);
    if (contenders.length > 1) {
      Logger.warn(
        'KeyboardShortcutManager',
        `Shortcut conflict for "${keyCombo}": ${contenders.length} active shortcuts at equal priority ${topPriority}:`,
        contenders.map(s => s.source).join(', ')
      );
    }
  }
  
  /**
   * Clean up resources and stop listening.
   */
  dispose(): void {
    this.stopListening();
    this.shortcuts.clear();
    this.keyMap.clear();
  }
}
